// Family / multi-profile management. Lists every patient identity the
// caller has access to (via UserPatientLink → PatientInfo), grouped into
// "You" (relation=SELF) and "Family" (everyone else). Lets the patient
// explicitly merge two or more profiles into a single PersonIdentity
// when they know the rows represent the same physical person across
// brands — there's no reliable automated detection (clinic-recorded
// names vary; DOB is derived from age on intake forms), so grouping
// is strictly user-driven.

import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePatient } from '../../context/PatientContext'
import { mergeIdentity } from '../../services/identitiesService'
import {
  BackLink,
  Card,
  CardTitle,
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  PrimaryButton,
  Row,
  Stack,
} from '../../layout/styledComponents'

const relationLabel = (relation) =>
  ({
    SELF: 'You',
    SPOUSE: 'Spouse',
    PARENT: 'Parent',
    CHILD: 'Child',
    SIBLING: 'Sibling',
    FRIEND: 'Friend',
    OTHER: 'Family',
  }[relation] || 'Family')

export default function FamilyProfilesPage() {
  const { profiles, currentProfileId, switchProfile, groupedProfiles, refreshProfiles } =
    usePatient()
  const navigate = useNavigate()

  const [mergeMode, setMergeMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [merging, setMerging] = useState(false)
  const [mergeError, setMergeError] = useState(null)

  const startMergeMode = useCallback(() => {
    setMergeMode(true)
    setSelectedIds(new Set())
    setMergeError(null)
  }, [])

  const cancelMergeMode = useCallback(() => {
    setMergeMode(false)
    setSelectedIds(new Set())
    setMergeError(null)
  }, [])

  const toggleSelect = useCallback((profileId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(profileId)) next.delete(profileId)
      else next.add(profileId)
      return next
    })
  }, [])

  const confirmMerge = useCallback(async () => {
    if (selectedIds.size < 2) return
    setMerging(true)
    setMergeError(null)
    try {
      await mergeIdentity({ profileIds: Array.from(selectedIds) })
      await refreshProfiles()
      setMergeMode(false)
      setSelectedIds(new Set())
    } catch {
      setMergeError('Could not merge — please try again.')
    } finally {
      setMerging(false)
    }
  }, [selectedIds, refreshProfiles])

  const renderRow = (p) => {
    const isSelected = selectedIds.has(p.id)
    return (
      <Card
        key={p.id}
        style={
          mergeMode && isSelected
            ? { borderColor: 'rgba(31, 58, 95, 0.5)', background: 'rgba(31, 58, 95, 0.04)' }
            : undefined
        }
      >
        <Row $between $gap="12px" $wrap>
          <Row $gap="10px" style={{ minWidth: 0, flex: 1 }}>
            {mergeMode ? (
              <input
                type="checkbox"
                aria-label={`Select ${p.displayName}`}
                checked={isSelected}
                onChange={() => toggleSelect(p.id)}
                style={{ marginTop: 4, transform: 'scale(1.2)' }}
              />
            ) : null}
            <Stack $gap="4px" style={{ minWidth: 0 }}>
              <CardTitle style={{ margin: 0 }}>{p.displayName}</CardTitle>
              <Muted>
                {p.brandName ? `${p.brandName} · ` : ''}
                {relationLabel(p.relation)}
                {p.patientNumber ? ` · #${p.patientNumber}` : ''}
              </Muted>
            </Stack>
          </Row>
          {!mergeMode ? (
            <Row $gap="8px">
              {p.id === currentProfileId ? (
                <Pill $tone="success">Active</Pill>
              ) : (
                <GhostButton onClick={() => switchProfile(p.id)}>Switch to</GhostButton>
              )}
            </Row>
          ) : null}
        </Row>
      </Card>
    )
  }

  return (
    <ContentMaxWidth>
      <Stack $gap="24px">
        <BackLink to="/patient-home/profile">Back to Profile</BackLink>
        <Row $between $gap="12px" $wrap>
          <div>
            <Pill>Family</Pill>
            <PageHeading style={{ marginTop: 12 }}>Profiles you manage</PageHeading>
            <PageSubheading>
              One login can manage multiple care profiles — yours across different clinic brands,
              plus family members. Switch any time from the avatar in the top bar.
            </PageSubheading>
          </div>
          {!mergeMode ? (
            <GhostButton onClick={startMergeMode}>Merge profiles…</GhostButton>
          ) : null}
        </Row>

        {mergeMode ? (
          <Card style={{ background: 'rgba(31, 58, 95, 0.04)' }}>
            <CardTitle>Mark profiles as the same person</CardTitle>
            <Muted style={{ marginTop: 6 }}>
              Select two or more profiles that represent the same physical person across brands.
              They'll be grouped under one identity in the portal — your clinic records stay
              separate. You can un-merge any time from the Identities page.
            </Muted>
            {mergeError ? (
              <Muted style={{ marginTop: 10, color: '#C24D49' }}>{mergeError}</Muted>
            ) : null}
            <Row $gap="8px" $wrap style={{ marginTop: 14 }}>
              <PrimaryButton
                disabled={selectedIds.size < 2 || merging}
                onClick={confirmMerge}
              >
                {merging
                  ? 'Merging…'
                  : selectedIds.size < 2
                  ? 'Select 2 or more'
                  : `Merge ${selectedIds.size} profiles`}
              </PrimaryButton>
              <GhostButton onClick={cancelMergeMode} disabled={merging}>
                Cancel
              </GhostButton>
            </Row>
          </Card>
        ) : null}

        {groupedProfiles.self.length > 0 && (
          <Stack $gap="12px">
            <Muted
              style={{
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontSize: 12,
              }}
            >
              You
            </Muted>
            {groupedProfiles.self.map(renderRow)}
          </Stack>
        )}

        {groupedProfiles.others.length > 0 && (
          <Stack $gap="12px">
            <Muted
              style={{
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontSize: 12,
              }}
            >
              Family
            </Muted>
            {groupedProfiles.others.map(renderRow)}
          </Stack>
        )}

        <Card>
          <CardTitle>Need a correction?</CardTitle>
          <Muted>
            Patient identity (name, date of birth, gender, mobile, email) is owned by the clinic.
            If something looks wrong, raise a correction request from the
            {' '}<a href="/patient-home/profile/personal">Personal details</a> page; the clinic /
            support team reviews and applies it.
          </Muted>
        </Card>

        <Card>
          <CardTitle>Why multiple profiles?</CardTitle>
          <Muted>
            One person may have separate care records at separate clinic brands — those stay
            separate by design. If two of these profiles really represent the same physical person
            (e.g. you registered yourself at two different clinics), tap “Merge profiles…” above
            to group them under a single identity in the portal. The portal can't auto-detect this
            reliably — clinic-recorded names vary in spelling and DOB is often derived from the
            age you stated on intake — so we leave it as a deliberate, user-driven action.
          </Muted>
        </Card>
      </Stack>
    </ContentMaxWidth>
  )
}
